import React, { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  LabelList,
  Tooltip,
} from 'recharts';
import _ from 'lodash';
import moment from 'moment';

import { formatDateTime, formatTimeHM } from '../../../../utils/Helpers';

const GroupedBar = ({ cubeResponse, annotation, valueAnnotation }) => {
  const [hiddenKeys, setHiddenKeys] = useState([]);

  const truncate = (text, width) =>
    text && text.length > width ? text.slice(0, width) + '…' : text || '';

  const formatWithValueAnnotation = value => {
    let formatter = null;
    if (valueAnnotation) {
      Object.keys(valueAnnotation).forEach(key => {
        if (valueAnnotation?.[key]?.meta?.isDateTimeField) {
          return formatDateTime(`${value}Z`);
        } else if (valueAnnotation[key]?.meta?.isTimeField) {
          return formatTimeHM(`${value}Z`);
        } else if (valueAnnotation?.[key]?.meta?.format) {
          formatter = eval(valueAnnotation[key].meta.format);
        }
      });
    }
    return formatter ? formatter(value) : value;
  };

  const formatWithAnnotation = (annot, key, value) => {
    if (value == null) {
      return '';
    } else if (annot?.[key]?.meta?.isDateTimeField) {
      return formatDateTime(`${value}Z`);
    } else if (annotation[key]?.meta?.isTimeField) {
      return formatTimeHM(`${value}Z`);
    } else if (annotation[key]?.type === 'time') {
      return moment(`${value}`).format('MM-DD-YYYY');
    }
    const formatter = annot?.[key]?.meta?.format
      ? eval(annot[key].meta.format)
      : null;
    return formatter ? formatter(value) : value;
  };

  const getMeasureKey = row => {
    const priorityKeys = Object.keys(row).filter(k =>
      /(sum|count|min|max|avg)/i.test(k)
    );
    if (priorityKeys.length) return priorityKeys[0];
    return Object.keys(row).find(k => !isNaN(Number(row[k])));
  };

  const chartData = useMemo(() => {
    if (!cubeResponse?.length) return { data: [], barKeys: [] };

    const grouped = {};
    const barKeysSet = new Set();

    cubeResponse.forEach(row => {
      const clonedRow = _.cloneDeep(row);
      const keys = Object.keys(clonedRow);

      const measureKey = getMeasureKey(clonedRow);
      if (!measureKey) return;

      const dimensionKeys = keys.filter(k => k !== measureKey);
      if (!dimensionKeys.length) return;

      const groupKey = dimensionKeys[0];
      const stackKey = dimensionKeys[1] || measureKey;

      const groupValue = clonedRow[groupKey] ?? 'Unknown';
      const formattedGroup = formatWithAnnotation(
        annotation,
        groupKey,
        groupValue
      );

      const stackValue = clonedRow[stackKey];
      const formattedStack = formatWithAnnotation(
        annotation,
        stackKey,
        stackValue
      );

      const numericValue =
        typeof clonedRow[measureKey] === 'number'
          ? clonedRow[measureKey]
          : +Number(clonedRow[measureKey]).toFixed(3);

      if (!grouped[formattedGroup])
        grouped[formattedGroup] = { name: formattedGroup };

      grouped[formattedGroup][formattedStack] =
        (grouped[formattedGroup][formattedStack] || 0) + numericValue;

      barKeysSet.add(formattedStack);
    });

    const allBarKeys = Array.from(barKeysSet);

    Object.values(grouped).forEach(row => {
      allBarKeys.forEach(key => {
        if (!(key in row)) row[key] = 0;
      });
    });

    return { data: Object.values(grouped), barKeys: allBarKeys };
  }, [cubeResponse, annotation]);

  if (!chartData.data?.length) return null;

  const colors = useMemo(() => {
    const map = {};
    chartData.barKeys.forEach(key => {
      let hash = 0;
      for (let i = 0; i < key?.toString().length; i++) {
        hash = key.toString().charCodeAt(i) + ((hash << 5) - hash);
      }
      const r = (hash >> 0) & 255;
      const g = (hash >> 8) & 255;
      const b = (hash >> 16) & 255;
      const pastel = v => Math.floor((v + 255) / 2);
      map[key] = `rgb(${pastel(r)},${pastel(g)},${pastel(b)})`;
    });
    return map;
  }, [chartData.barKeys]);

  const toggleLegend = (key, e) => {
    e.stopPropagation();
    e.preventDefault();
    setHiddenKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const renderLegend = props => {
    const { payload } = props;
    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 12,
          paddingBottom: 10,
        }}
      >
        {payload.map(entry => {
          const isHidden = hiddenKeys.includes(entry.dataKey);
          // Legend labels are dimension keys (e.g. "$ 50.00"), not measure values.
          const formattedValue = entry.dataKey ?? entry.value;
          return (
            <span
              key={entry.dataKey}
              onClick={e => toggleLegend(entry.dataKey, e)}
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 500,
                textDecoration: isHidden ? 'line-through' : 'none',
                opacity: isHidden ? 0.5 : 1,
              }}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  borderRadius: 2,
                  display: 'inline-block',
                }}
              />
              {formattedValue}
            </span>
          );
        })}
      </div>
    );
  };

  const MIN_BAR_HEIGHT_FOR_LABEL = 20;

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={chartData.data}
        margin={{ top: 40, right: 20, left: 0, bottom: 5 }}
        barCategoryGap="20%"
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip
          formatter={(value, name) => [formatWithValueAnnotation(value), name]}
        />
        <Legend verticalAlign="top" align="center" content={renderLegend} />
        {chartData.barKeys.map(key => (
          <Bar
            key={key}
            dataKey={key}
            fill={colors[key]}
            radius={[4, 4, 0, 0]}
            hide={hiddenKeys.includes(key)}
          >
            <LabelList
              dataKey={key}
              content={props => {
                const { x, y, width, height, value } = props;
                if (
                  value == null ||
                  height < MIN_BAR_HEIGHT_FOR_LABEL ||
                  width < 25
                )
                  return null;
                return (
                  <text
                    x={x + width / 2}
                    y={y + height / 2}
                    fill="#000"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{ fontSize: 12, fontWeight: 500 }}
                  >
                    {truncate(formatWithValueAnnotation(value), 5)}
                  </text>
                );
              }}
            />
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default GroupedBar;
